import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { Button, Icon, Loader, Message } from "semantic-ui-react";
import moment from "moment";
import apis from "../../../../../../../../../apis";
import { byteArrayToUrl } from "../../../../../../../../../utils";
import "./BotScreenshots.styles.scss";

const TIMESTAMP_FORMAT = "DD-MMM-YYYY HH:mm:ss";

function addToSet(set, key) {
  const next = new Set(set);
  next.add(key);
  return next;
}

function removeFromSet(set, key) {
  const next = new Set(set);
  next.delete(key);
  return next;
}

async function fetchImageFromApi(key) {
  const response = await apis.applicantDocumentHandler.getFile(key);
  return byteArrayToUrl(response.data.buffer.data, "image/png");
}

function ImageItem({ imageObject, imageUrl, isLoading, hasFailed, onRetry }) {
  const formattedTimestamp = moment(imageObject.createdAt).format(TIMESTAMP_FORMAT);

  function renderImageContent() {
    if (isLoading) {
      return (
        <div className="bot-screenshots__loading" aria-busy="true">
          <Loader active inline="centered" />
        </div>
      );
    }

    if (hasFailed) {
      return (
        <div className="bot-screenshots__error" role="alert">
          <Icon name="warning sign" size="big" />
          <p>Failed to load image</p>
          <Button size="small" onClick={onRetry}>
            Retry
          </Button>
        </div>
      );
    }

    return (
      <img
        src={imageUrl}
        alt={`Bot screenshot at ${formattedTimestamp}`}
        className="bot-screenshots__image"
      />
    );
  }

  return (
    <div className="bot-screenshots__item">
      {renderImageContent()}
      <div className="bot-screenshots__timestamp">{formattedTimestamp}</div>
    </div>
  );
}

ImageItem.propTypes = {
  imageObject: PropTypes.shape({
    key: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
  }).isRequired,
  imageUrl: PropTypes.string,
  isLoading: PropTypes.bool,
  hasFailed: PropTypes.bool.isRequired,
  onRetry: PropTypes.func.isRequired,
};

function BotScreenshots({ imageObjects = [] }) {
  const [imageCache, setImageCache] = useState({});
  const [loadingKeys, setLoadingKeys] = useState(new Set());
  const [failedKeys, setFailedKeys] = useState(new Set());
  const blobUrlsRef = useRef([]);
  const fetchInitiatedRef = useRef(new Set());

  function fetchImage(key) {
    fetchInitiatedRef.current.add(key);
    setLoadingKeys((prev) => addToSet(prev, key));
    setFailedKeys((prev) => removeFromSet(prev, key));

    fetchImageFromApi(key)
      .then((url) => {
        blobUrlsRef.current.push(url);
        setImageCache((prev) => ({ ...prev, [key]: url }));
      })
      .catch((error) => {
        console.error(`Failed to fetch image with key ${key}:`, error);
        fetchInitiatedRef.current.delete(key);
        setFailedKeys((prev) => addToSet(prev, key));
      })
      .finally(() => {
        setLoadingKeys((prev) => removeFromSet(prev, key));
      });
  }

  useEffect(() => {
    imageObjects.forEach(({ key }) => {
      const alreadyFetched = fetchInitiatedRef.current.has(key);
      if (!alreadyFetched) {
        fetchImage(key);
      }
    });
    // fetchImage uses only refs and stable setters, so it does not need to be a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageObjects]);

  useEffect(() => {
    const urlsToCleanup = blobUrlsRef.current;
    return () => {
      urlsToCleanup.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const sortedImageObjects = useMemo(
    () =>
      [...imageObjects].sort((a, b) =>
        moment(b.createdAt).diff(moment(a.createdAt))
      ),
    [imageObjects]
  );

  if (imageObjects.length === 0) {
    return (
      <Message info className="bot-screenshots__empty">
        <Message.Header>No Screenshots Available</Message.Header>
        <p>No bot progress images to display.</p>
      </Message>
    );
  }

  return (
    <div className="bot-screenshots__container">
      {sortedImageObjects.map((item, index) => (
        <ImageItem
          key={item.key || index}
          imageObject={item}
          imageUrl={imageCache[item.key]}
          isLoading={loadingKeys.has(item.key)}
          hasFailed={failedKeys.has(item.key)}
          onRetry={() => fetchImage(item.key)}
        />
      ))}
    </div>
  );
}

BotScreenshots.propTypes = {
  imageObjects: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      createdAt: PropTypes.string.isRequired,
    })
  ),
};

export default BotScreenshots;
